import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import SummaryAnalysis from "@/components/analysisSummary";
import DatasetExplorerPopup from "@/components/dataset/datasetExplorer";
import DatasetTabularModal from "@/components/dataset/datasetTable";
import { NpsModal } from "@/components/feedback/NpsModal";
import ComparePopup from "@/components/map/compare";
import ComparePanel from "@/components/map/compare/ComparePanel";
import ComparsionLimitAlert from "@/components/map/ComparsionLimitAlert";
import DatasetWidgetPopup from "@/components/map/DatasetWidgetPopup";
import DrawLimitInfo from "@/components/map/DrawLimitInfo";
import { DrawSubToolPopup } from "@/components/map/DrawSubToolPopup";
import { FeatureInfo, SelectedAttribute } from "@/components/map/FeatureInfo";
import LegendsInfo from "@/components/map/LegendsInfo";
import MapMenuControl from "@/components/map/menuControl";
import MapPreviewMenu from "@/components/map/preview";
import { TimeSeriesModal } from "@/components/map/TimeSeries";
import SearchLocationPopup from "@/components/searchLocation";
import { LocationInfoPopup } from "@/components/searchLocation/locationInfoPopup";
import AdminAreaSettingsPopup from "@/components/spatialAnalysis/adminAreaSettings";
import AnalysisConfigViewOnly from "@/components/spatialAnalysis/analysisConfigViewOnly";
import AnalysisJobList from "@/components/spatialAnalysis/analysisJobList";
import AnalysisResultPopup from "@/components/spatialAnalysis/analysisResultPopup";
import CatchmentSettingsPopup from "@/components/spatialAnalysis/catchmentSettings";
import DataWeightSettingsPopupV2 from "@/components/spatialAnalysis/dataWeightSettingsV2";
import PolygonSettingsPopup from "@/components/spatialAnalysis/polygonSettings";
import DrawPolygonPanel from "@/components/spatialAnalysis/polygonSettings/drawPolygonPanel";
import SpatialAnalysisPopup from "@/components/spatialAnalysis/spatialAnalysisPopup";
import SpatialAnalysisTabularModal from "@/components/spatialAnalysis/spatialAnalysisTable";
import { TemplateModal } from "@/components/template";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup
} from "@/components/ui/resizable";
import { MESSAGE } from "@/constants/message";
import { useNpsTrigger } from "@/hooks/feedback/useNpsTrigger";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAnalysisResult } from "@/hooks/spatialAnalysis/useAnalysisResult";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useHandleClearView from "@/hooks/ui/useHandleClearView";
import useAnalysisJob from "@/hooks/useAnalysisJob";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  clearAllGlobalFilterLinks,
  setAnalysisId
} from "@/store/slice/datasetSlice";
import {
  applyAdminTreeGlobalFilterTemp,
  resetGlobalFilter,
  setAdminAreaSettingsDataTemp as setGlobalFilterAdminAreaSettingsDataTemp,
  setSelectedAreaIdsTemp
} from "@/store/slice/globalFilterSlice";
import {
  clearCatchmentSettingsTemp,
  clearPolygonSettingsTemp,
  setAdminAreaAddress,
  setAdminAreaSettingsData,
  setAdminAreaSettingsDataTemp,
  setAdminTreeSettingsSelectedIds,
  setAreaSize,
  setOverrideGlobalFilter
} from "@/store/slice/spatialSlice";
import {
  ESidebarMenuType,
  resetUIDataConfigExceptSidebarMenu,
  setActiveSpatialAnalysisPanel,
  setShowNpsModal,
  setShowPolygonPopup,
  toggleAllowPinMarker,
  toggleAnalysisJobList,
  toggleAnalysisResultPopup,
  toggleAreaAdminPopup,
  toggleCatchmentPopup,
  toggleDatasetExplorerPopup,
  toggleDatasetWidgetPopup,
  toggleDataWeightSettingsPopup,
  toggleGlobalFilterPopup,
  toggleLegendInfo,
  toggleLegendVisualization
} from "@/store/slice/uiSlice";
import { AreaInputType } from "@/types/spatial";

export const HomeContent = () => {
  const dispatch = useDispatch();
  const [selectedAttribute, setSelectedAttribute] =
    useState<SelectedAttribute | null>(null);

  const {
    isTemplateEnabled,
    isDrawPolygonEnabled,
    isMultipleSearchTableDataset,
    isGlobalFilterEnabled,
    isAdminTreeEnabled,
    isNpsModalEnabled,
    isTimeSeriesChartEnabled,
    isGlobalFilterOverrideEnabled,
    isAnalysisConfigViewOnlyEnabled
  } = useAppFeatures();
  const [searchParams] = useSearchParams();
  const { showConfirmation } = useConfirmationModal();
  const {
    handleResetDatasetToHistoryDataset,
    removeAllCatchmentMarkers,
    renderDrawPolygon
  } = useMap();
  const { handleCloseDetailView } = useHandleClearView();
  const containerRef = useRef<HTMLDivElement>(null);

  useAnalysisResult();
  useNpsTrigger();

  const selectedFeature = useSelector(
    (state: RootState) => state.map.selectedFeature
  );

  const {
    showCatchmentPopup,
    showAreaAdminPopup,
    showPolygonPopup,
    showDatasetExplorerPopup,
    showDataWeightSettingsPopup,
    sideBarMenuTypePopupDisplay,
    showAnalysisResultPopup,
    showComparisonPopup,
    showSpatialAnalysisResultTabularModal,
    showLegendInfo,
    showAnalysisSummaryPopup,
    showSearchLocationPopup,
    markerInfo,
    isDrawPolygon,
    showAnalysisJobList,
    showGlobalFilterPopup,
    showNpsModal,
    showDatasetWidgetPopup
  } = useSelector((state: RootState) => state.ui);

  const { spatialConfig, overrideGlobalFilter, latestAnalysisId } = useSelector(
    (state: RootState) => state.spatial
  );

  const { showNpsModal: savedShowNpsModal } = useSelector(
    (state: RootState) => state.ui
  );

  const {
    setContainer,
    handleMapFullScreen,
    zoomLevel,
    map,
    synchronizeFeatures,
    enterDrawMode,
    enterSelectMode,
    drawMode
  } = useMap();

  const [datasetTabularModalShow, setDatasetTabularModalShow] =
    useState<boolean>(false);

  const onToggleLegendVisibility = () => {
    dispatch(toggleLegendInfo());
  };

  const onOpenDatasetTabularModal = () => {
    setDatasetTabularModalShow(true);
  };

  const onCloseCatchmentPopup = () => {
    dispatch(toggleAllowPinMarker(false));
    dispatch(clearCatchmentSettingsTemp());
    dispatch(toggleCatchmentPopup(false));
  };

  const onToggleLegendVisualization = () => {
    dispatch(toggleLegendVisualization());
  };

  const onToggleAnalysisJobList = () => {
    dispatch(toggleAnalysisJobList());
    toast.dismiss("spatial-analysis-toast");
    if (showDatasetWidgetPopup) {
      dispatch(toggleDatasetWidgetPopup());
    }
  };

  const onToggleDatasetWidget = () => {
    dispatch(toggleDatasetWidgetPopup());
    if (showAnalysisJobList) {
      dispatch(toggleAnalysisJobList());
    }
  };

  const handleCloseAdminAreaPopup = () => {
    dispatch(toggleAreaAdminPopup(false));
    dispatch(setAdminAreaSettingsDataTemp(null));
  };

  const handleCloseGlobalFilterPopup = () => {
    if (isGlobalFilterEnabled) {
      if (isAdminTreeEnabled) {
        dispatch(setSelectedAreaIdsTemp([]));
      }
      dispatch(setGlobalFilterAdminAreaSettingsDataTemp(null));
      dispatch(toggleGlobalFilterPopup(false));
    }
  };

  const onOpenGlobalFilter = () => {
    if (isGlobalFilterEnabled) {
      if (!showGlobalFilterPopup) dispatch(applyAdminTreeGlobalFilterTemp());
      dispatch(setActiveSpatialAnalysisPanel("showGlobalFilterPopup"));
    }
  };

  const onClearGlobalFilter = () => {
    if (!isGlobalFilterOverrideEnabled) {
      dispatch(clearAllGlobalFilterLinks());
    }
    dispatch(resetGlobalFilter());
    dispatch(setAreaSize(0));

    if (!overrideGlobalFilter) {
      dispatch(
        setAdminAreaAddress({
          lv0: null,
          lv1: null,
          lv2: null,
          lv3: null,
          lv4: null
        })
      );
      dispatch(setAdminAreaSettingsData(null));
      if (isAdminTreeEnabled) {
        dispatch(setAdminTreeSettingsSelectedIds([]));
      }
      dispatch(setOverrideGlobalFilter(true));
    }
  };

  const handleClosePolygonPopup = (isSave?: boolean) => {
    if (!isSave) synchronizeFeatures();

    dispatch(setShowPolygonPopup(false));
    dispatch(clearPolygonSettingsTemp());
  };

  const handleCloseSpatialPopup = () => {
    dispatch(resetUIDataConfigExceptSidebarMenu());
    // Persist showNpsModal state when closing spatial analysis popup
    if (savedShowNpsModal) {
      dispatch(setShowNpsModal(true));
    }
  };

  const handleCloseAnalysisResultPopup = () => {
    showConfirmation({
      title: "Unsaved Changes",
      message: MESSAGE.CONFIRMATION_LEAVE,
      cancelLabel: "",
      confirmLabel: "Leave",
      onConfirm: () => {
        if (spatialConfig?.areaInput?.value === AreaInputType.POLYGON) {
          renderDrawPolygon();
        }
        toast.dismiss();
        handleCloseDetailView();
        removeAllCatchmentMarkers();
        handleResetDatasetToHistoryDataset();
        dispatch(toggleAnalysisResultPopup());
        dispatch(setAnalysisId(null));
        dispatch(setShowNpsModal(false));
      }
    });
  };

  const handleSetSelectedAttribute = (value: SelectedAttribute | null) => {
    setSelectedAttribute(value);

    setTimeout(() => {
      setSelectedAttribute(null);
    }, 150);
  };

  useEffect(() => {
    if (containerRef.current) setContainer(containerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPreviewMode = searchParams.get("preview") === "true";

  useAnalysisJob();

  return (
    <div className="relative flex flex-col w-full h-screen">
      <ResizablePanelGroup direction="vertical">
        <ResizablePanel className="flex" defaultSize={75}>
          <div
            ref={containerRef}
            data-testid="map-container"
            className="relative flex-1"
          >
            {!showComparisonPopup.isShow && (
              <MapMenuControl
                zoomLevel={zoomLevel}
                isLegendVisible={showLegendInfo ?? false}
                handleMapFullScreen={handleMapFullScreen}
                onZoomIn={() => map?.zoomIn()}
                onZoomOut={() => map?.zoomOut()}
                onToggleLegendVisibility={onToggleLegendVisibility}
                onToggleLegendVisualization={onToggleLegendVisualization}
                onToggleAnalysisJobList={onToggleAnalysisJobList}
                onToggleDatasetWidget={onToggleDatasetWidget}
                isDatasetWidgetVisible={showDatasetWidgetPopup}
              />
            )}
          </div>
          {!showComparisonPopup.isShow && isDrawPolygon && (
            <DrawSubToolPopup
              drawMode={drawMode}
              enterSelectMode={enterSelectMode}
              enterDrawMode={enterDrawMode}
              showLegendInfo={showLegendInfo}
            />
          )}
          {!showComparisonPopup.isShow &&
            showLegendInfo &&
            !datasetTabularModalShow &&
            !showDatasetExplorerPopup &&
            !showSpatialAnalysisResultTabularModal && <LegendsInfo />}
          <FeatureInfo
            selectedFeature={selectedFeature}
            setSelectedAttribute={handleSetSelectedAttribute}
          />
          {datasetTabularModalShow && (
            <DatasetTabularModal
              isOpen={datasetTabularModalShow}
              onClose={() => setDatasetTabularModalShow(false)}
              isMultipleSearchTableDataset={isMultipleSearchTableDataset}
            />
          )}
          {showSpatialAnalysisResultTabularModal && (
            <SpatialAnalysisTabularModal
              selectedFeature={selectedFeature}
              isMultipleSearchTableDataset={isMultipleSearchTableDataset}
              selectedAttribute={selectedAttribute}
            />
          )}
          {showCatchmentPopup && (
            <CatchmentSettingsPopup
              onClose={onCloseCatchmentPopup}
              onOpenDatasetTabularModal={onOpenDatasetTabularModal}
            />
          )}
          {isGlobalFilterEnabled && showGlobalFilterPopup && (
            <AdminAreaSettingsPopup
              type="globalFilter"
              onClose={handleCloseGlobalFilterPopup}
            />
          )}
          {showAreaAdminPopup && (
            <AdminAreaSettingsPopup
              type="spatial"
              onClose={handleCloseAdminAreaPopup}
            />
          )}
          {showPolygonPopup && (
            <PolygonSettingsPopup onClose={handleClosePolygonPopup} />
          )}
          {showDatasetExplorerPopup && (
            <DatasetExplorerPopup
              isOpen
              onClose={() => dispatch(toggleDatasetExplorerPopup(false))}
            />
          )}
          {showDataWeightSettingsPopup && (
            <DataWeightSettingsPopupV2
              onClose={() => dispatch(toggleDataWeightSettingsPopup(false))}
            />
          )}
          {showAnalysisJobList && <AnalysisJobList />}
          {showDatasetWidgetPopup && !showAnalysisResultPopup && (
            <DatasetWidgetPopup onToggleDatasetWidget={onToggleDatasetWidget} />
          )}
          {sideBarMenuTypePopupDisplay === ESidebarMenuType.SPATIAL && (
            <SpatialAnalysisPopup
              onClose={handleCloseSpatialPopup}
              onOpenDatasetExplorer={() =>
                dispatch(
                  setActiveSpatialAnalysisPanel("showDatasetExplorerPopup")
                )
              }
              onOpenDatasetTabularModal={onOpenDatasetTabularModal}
              onOpenGlobalFilter={onOpenGlobalFilter}
              onClearGlobalFilter={onClearGlobalFilter}
            />
          )}
          {showComparisonPopup.isShow && <ComparePopup />}
          {isPreviewMode && <MapPreviewMenu />}
          {/* Read-only Analysis Config beside the result. Keyed off
              `showAnalysisResultPopup` alone so it covers every way the result
              opens (Generate Results, Analysis Job List, History page); that
              flag is also what clears `sideBarMenuTypePopupDisplay`, so this
              never renders alongside the editable panel above. */}
          {isAnalysisConfigViewOnlyEnabled && showAnalysisResultPopup && (
            <AnalysisConfigViewOnly />
          )}
          {showAnalysisResultPopup && (
            <AnalysisResultPopup onClose={handleCloseAnalysisResultPopup} />
          )}

          {showAnalysisSummaryPopup && <SummaryAnalysis />}
          {isTemplateEnabled && <TemplateModal />}
          {showSearchLocationPopup && <SearchLocationPopup />}
          {markerInfo && <LocationInfoPopup data={markerInfo} />}

          {isDrawPolygon && isDrawPolygonEnabled && <DrawPolygonPanel />}
          <DrawLimitInfo />
          {isNpsModalEnabled && showNpsModal && <NpsModal />}

          {isTimeSeriesChartEnabled && !latestAnalysisId && (
            <TimeSeriesModal
              selectedFeature={selectedFeature}
              forceCollapse={showComparisonPopup.isShow || isDrawPolygon}
            />
          )}
        </ResizablePanel>
        {showComparisonPopup.isShow && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel defaultValue={25} maxSize={75} minSize={25}>
              <ComparePanel />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
      <ComparsionLimitAlert />
    </div>
  );
};

export default function HomePage() {
  return <HomeContent />;
}
